import { useRestApiProvider } from "@jugl-web/rest-api";
import React, { useCallback, useMemo } from "react";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { TwilioAccountContext } from "./TwilioAccountContext";

export const TwilioAccountProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();

  const { data: crmConfig, isLoading: isTwilioConfigLoading } =
    integrationsApi.useGetCRMConfigQuery({ entityId });

  const [listTwilioPhoneNumbersMutation, { isLoading: isListingPhoneNumbers }] =
    integrationsApi.useListTwilioPhoneNumbersMutation();
  const [
    connectTwilioAccountMutation,
    { isLoading: isConnectingTwilioAccount },
  ] = integrationsApi.useConnectTwilioAccountMutation();
  const [changeTwilioPhoneMutation, { isLoading: isChangingTwilioPhone }] =
    integrationsApi.useChangeTwilioPhoneMutation();
  const [
    disconnectTwilioAccountMutation,
    { isLoading: isDisconnectingTwilioAccount },
  ] = integrationsApi.useDisconnectTwilioAccountMutation();

  const twilioAccount = crmConfig?.twilio ?? undefined;
  const isConnected = !!twilioAccount?.id;

  const listPhoneNumbers = useCallback(
    ({ sid, token }: { sid: string; token: string | null }) =>
      listTwilioPhoneNumbersMutation({
        entityId,
        sid,
        token,
        meta: { silent: true },
      }).unwrap(),
    [listTwilioPhoneNumbersMutation, entityId]
  );

  const connectTwilioAccount = useCallback(
    (params: {
      sid: string;
      token: string;
      phone_number: string;
      phone_number_sid: string;
    }) =>
      connectTwilioAccountMutation({
        entityId,
        ...params,
        meta: { silent: true },
      }).unwrap(),
    [connectTwilioAccountMutation, entityId]
  );

  const changeTwilioPhone = useCallback(
    (params: { phone_number: string; phone_number_sid: string }) =>
      changeTwilioPhoneMutation({
        entityId,
        ...params,
        meta: { silent: true },
      }).unwrap(),
    [changeTwilioPhoneMutation, entityId]
  );

  const disconnectTwilioAccount = useCallback(async () => {
    await disconnectTwilioAccountMutation({
      entityId,
      meta: { silent: true },
    }).unwrap();
  }, [disconnectTwilioAccountMutation, entityId]);

  const contextValue = useMemo(
    () => ({
      twilioAccount,
      isConnected,
      isTwilioConfigLoading,
      listPhoneNumbers,
      isListingPhoneNumbers,
      connectTwilioAccount,
      isConnectingTwilioAccount,
      changeTwilioPhone,
      isChangingTwilioPhone,
      disconnectTwilioAccount,
      isDisconnectingTwilioAccount,
    }),
    [
      twilioAccount,
      isConnected,
      isTwilioConfigLoading,
      listPhoneNumbers,
      isListingPhoneNumbers,
      connectTwilioAccount,
      isConnectingTwilioAccount,
      changeTwilioPhone,
      isChangingTwilioPhone,
      disconnectTwilioAccount,
      isDisconnectingTwilioAccount,
    ]
  );

  return (
    <TwilioAccountContext.Provider value={contextValue}>
      {children}
    </TwilioAccountContext.Provider>
  );
};
